/**
 * What the sheet is showing.
 *
 * One branch per view, each owning its own data. Nothing writes the body's
 * contents from outside any more: opening a panel sets the view, and the
 * branch that wins asks for what it needs.
 */

import { type Component, Match, Show, Switch } from 'solid-js';
import { queue } from '../../state/player.ts';
import { sheet, sheetOpenings } from '../../state/ui.ts';
import { LibraryPanel } from './LibraryPanel.tsx';
import { PlaylistPanel } from './PlaylistPanel.tsx';
import { SearchPanel } from './SearchPanel.tsx';
import { Empty } from './SheetStates.tsx';
import { TrackList } from './TrackList.tsx';

const QueuePanel: Component = () => (
  <Show
    when={queue().length}
    fallback={<Empty title="Queue is empty" body="Pick a playlist to fill it." />}
  >
    <TrackList tracks={queue()} header={`Up next — ${queue().length} records`} />
  </Show>
);

/* Keyed on the opening count, so every trip to a panel is a fresh subtree.
   That is what makes reopening one that failed an actual retry rather than a
   second look at an error boundary that has already latched. */
export const SheetBody: Component = () => (
  <Show when={sheetOpenings()} keyed>
    <Switch>
      <Match when={sheet().view === 'home'}>
        <LibraryPanel />
      </Match>
      {/* Keyed again: each playlist gets its own panel and its own boundary. */}
      <Match when={sheet().view === 'playlist' && sheet().playlist} keyed>
        {(playlist) => <PlaylistPanel playlist={playlist} />}
      </Match>
      <Match when={sheet().view === 'search'}>
        <SearchPanel />
      </Match>
      <Match when={sheet().view === 'queue'}>
        <QueuePanel />
      </Match>
    </Switch>
  </Show>
);
